/* Reading a published legal document — STORY-048.
 *
 * Deliberately outside the application shell and outside any workspace. The
 * people who need this page most are the ones who are not signed in: somebody
 * deciding whether to create an account, and a data subject exercising a right
 * under the Privacy Policy who is not our customer at all and never will be.
 */

import type { LegalDocumentSlug } from '@eventuley/contracts';
import { Link } from '@tanstack/react-router';
import { useTranslate } from '../../../shared/i18n/index.js';
import { useLegalDocument } from '../api/index.js';
import { DocumentBlocks } from '../components/document-blocks.js';

export function LegalDocumentScreen({ slug }: { slug: LegalDocumentSlug | undefined }) {
  const t = useTranslate();
  /* An unknown slug must never fall back to a different document — showing the
   * Terms to somebody who asked for the DPA is worse than showing nothing. */
  const document = useLegalDocument(slug);

  return (
    <main className="legal">
      <Link to="/sign-in" className="legal__back">
        {t('legal.back')}
      </Link>

      {slug !== undefined && document.isPending && (
        <p className="legal__status">{t('legal.loading')}</p>
      )}

      {(slug === undefined || document.isError) && (
        /* One message for "no such document" and "not published yet". The
         * difference is not the reader's business, and naming it would announce
         * that an unpublished contract exists. */
        <>
          <h1>{t('legal.unavailable_title')}</h1>
          <p className="legal__status">{t('legal.unavailable_body')}</p>
        </>
      )}

      {document.data && (
        <article>
          <h1>{document.data.title}</h1>
          {/* The version is on screen, not only in the payload. It is the string
           * recorded against an acceptance, so somebody asking "what did I
           * agree to" can check the answer rather than take it on trust. */}
          <p className="legal__version">
            {t('legal.version', {
              version: document.data.version,
              date: document.data.effectiveDate,
            })}
          </p>
          <DocumentBlocks blocks={document.data.blocks} />
        </article>
      )}
    </main>
  );
}
